import { ISubjectGoldPrice, ISubjectVote } from "api/types/_campaign";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import CopyWrapper from "components/Common/CopyWrapper";
import DropdownOption from "components/Common/DropdownOption";
import { useFormik } from "formik";
import { AI_MODEL_OPTIONS, TEMPLATE_CATEGORY_TYPE_BOOSTING_CONTENT, TEMPLATE_ELEMENT_TYPE_BOOSTING_CONTENT } from "helpers/constans";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import 'react-json-pretty/themes/monikai.css';
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import { Col, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap";
import { postSubjectVote, putSubjectVote } from "store/campaign/thunk";
import * as Yup from "yup";

export interface Props {
    isOpen?: boolean;
    detail?: ISubjectVote | null,
    variables: any[],
    onClose?: () => void
    triggerRefresh?: (type?: string) => void;
}

const SubjectForm = ({
    isOpen = false,
    detail = null,
    variables = [],
    onClose,
    triggerRefresh
}: Props) => {
    const { t } = useTranslation();

    const AI_MODEL_OPTIONS_LANG: any = AI_MODEL_OPTIONS?.map((item: any) => ({ value: item?.value, label: t(item?.label) }));

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [search, setSearch] = useState<string>('');

    const [isFullView, setIsFullView] = useState<boolean>(false);

    const onCloseClick = () => {
        onClose && onClose();
    }
    const handleSubmit = async (values: any) => {
        try {
            setIsLoading((_prev) => true);
            const data = {
                name: values?.name || '',
                content: values?.content || '',
                generate_via_ai: Number(values?.generate_via_ai || 0),
                generate_prompt: values?.generate_prompt || '',
                generate_ai_model: values?.generate_ai_model?.value || AI_MODEL_OPTIONS_LANG[1]?.value,
                ...(!!detail?.id ? {} : { category: TEMPLATE_CATEGORY_TYPE_BOOSTING_CONTENT.VOTE, element_type: TEMPLATE_ELEMENT_TYPE_BOOSTING_CONTENT.TITLE, })
            };
            const response: any = !!detail?.id ? await putSubjectVote(detail?.id, data) : await postSubjectVote(data);
            if (response?.code === 200) {
                setIsLoading((_prev) => false);
                toast(
                    `${t("The process has been completed.")}`,
                    CONFIG_OPTION_TOAST_NORMAL
                );
                triggerRefresh && triggerRefresh();
            } else {
                setIsLoading((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({
        name: Yup.string().required(`${t("This is required")}`),
        content: Yup.string().required(`${t("This is required")}`),
        generate_via_ai: Yup.number().nullable(),
        generate_prompt: Yup.string().nullable(),
        generate_ai_model: Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
        }).required(`${t("This is required")}`),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            content: '',
            generate_via_ai: 0,
            generate_prompt: '',
            generate_ai_model: AI_MODEL_OPTIONS_LANG[1],
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    useEffect(() => {
        const handleCallAllOption = async () => {
            try {
                const _name = detail?.name || '';
                const _content = detail?.content || '';
                const _generate_via_ai = Number(detail?.generate_via_ai || 0);
                const _generate_prompt = detail?.generate_prompt || '';
                const _generate_ai_model = AI_MODEL_OPTIONS_LANG?.find((_m: any) => (String(detail?.generate_ai_model) === String(_m?.value))) || AI_MODEL_OPTIONS_LANG[1];

                formik.setFieldValue('name', _name);
                formik.setFieldValue('content', _content);
                formik.setFieldValue('generate_via_ai', _generate_via_ai);
                formik.setFieldValue('generate_prompt', _generate_prompt);
                formik.setFieldValue('generate_ai_model', _generate_ai_model);

                setTimeout(() => {
                    formik.setErrors({});
                }, 300);
            } catch (error: any) {
                // setIsLoadingDetail((_prev) => false);
                return error;
            }
        };
        handleCallAllOption();
    }, [JSON.stringify(detail)]);

    const _variables = useMemo(() => {
        return variables?.filter((item: any) => ((String(item?.value).toUpperCase()?.includes(String(search).toUpperCase()) || String(item?.description).toUpperCase()?.includes(String(search).toUpperCase()) || !search)));
    }, [search, JSON.stringify(variables)]);

    return (
        <React.Fragment>
            <Modal
                isOpen={isOpen}
                centered={true}
                size="xl"
                className={`border-header-footer modal-fullscreen`}
                scrollable={false}
                toggle={onCloseClick}
                keyboard={true}
            >
                <ModalHeader toggle={onCloseClick} className="modal-title-flex-grow-1">
                    <div className="d-flex flex-column gap-0 gap-sm-2 fs-18 fs-md-20">
                        <div className="flex-grow-1">
                            <>{!!detail?.id ? t('Button Update Subject') : t('Button Create Subject')}</>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="position-relative">
                    <div>
                        <form onSubmit={formik.handleSubmit}>
                            <Row>
                                <Col lg={isFullView ? 2 : 5} className="order-2 order-lg-1 mt-4 mt-lg-0">
                                    <div className="color-scrollbar-os table-responsive w-100" style={{ height: '100%', maxHeight: '770px', overflow: 'auto', borderRadius: 'var(--vz-border-radius)', border: ' var(--vz-border-width) solid var(--vz-input-border-custom)' }}>
                                        <table className="table table-borderless table-light table-hover table-nowrap mb-0">
                                            <thead className="table-primary" style={{ position: 'sticky', top: 0 }}>
                                                <tr>
                                                    <th style={{ width: '200px' }}>
                                                        <div className="d-flex align-items-center">
                                                            {t('Key')}
                                                            <div className="app-search p-0 ps-2">
                                                                <div className="position-relative" style={{
                                                                    width: '190px'
                                                                }}>
                                                                    <Input type="text" className="form-control bg-white" placeholder={`${t('Button Search')}...`}
                                                                        id="search-options"
                                                                        autoComplete="off"
                                                                        value={search}
                                                                        onChange={e => {
                                                                            setSearch((prev) => e.target.value);
                                                                        }}
                                                                    />
                                                                    <span style={{
                                                                        position: 'absolute',
                                                                        bottom: '2px',
                                                                        right: '5px',
                                                                        fontSize: '10px',
                                                                    }} className="text-danger">{_variables?.length || 0} {t('Results')} </span>
                                                                    <span className="mdi mdi-magnify search-widget-icon"></span>
                                                                    <span className="mdi mdi-close-circle search-widget-icon search-widget-icon-close d-none"
                                                                        id="search-close-options"></span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </th>
                                                    <th style={{ minWidth: '200px' }} className="align-middle">
                                                        {t('Example')}
                                                    </th>
                                                    <th style={{ minWidth: '200px' }} className="align-middle">
                                                        {t('Description')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(_variables || [])?.map((_key: any) => (
                                                    <tr key={_key?.id}>
                                                        <td>
                                                            <CopyWrapper contentCopy={_key?.value || ''} isShow={true}> {_key?.value || ''}</CopyWrapper>
                                                        </td>
                                                        <td>
                                                            {_key?.sample || '-'}
                                                        </td>
                                                        <td>
                                                            {_key?.description || '-'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div style={{
                                        position: 'absolute',
                                        top: '0px',
                                        right: 1,
                                        width: '45px',
                                        height: '45px',
                                        fontSize: '30px',
                                        textAlign: 'center',
                                        verticalAlign: 'middle',
                                        backgroundColor: 'var(--vz-primary)',
                                        color: 'var(--vz-white)',
                                        transform: 'translateX(-12px)',
                                        borderTopLeftRadius: '22px',
                                        borderBottomLeftRadius: '22px',
                                        borderTopRightRadius: '7px',
                                        cursor: 'pointer',
                                        boxShadow: '0px 0px 20px 0px rgba(76, 87, 125, 0.02)'
                                    }}
                                        onClick={() => setIsFullView((prev) => !prev)}
                                    >
                                        {isFullView ? <i className="mdi mdi-arrow-right-circle-outline" /> : <i className="mdi mdi-arrow-left-circle-outline" />}
                                    </div>
                                </Col>

                                <Col lg={isFullView ? 10 : 7} className="order-1 order-lg-2">
                                    <Col sm={12} className="mb-3">
                                        <Label htmlFor="name" className="form-label">{t('Name')} <span className="text-danger"> *</span></Label>
                                        <Input
                                            name="name"
                                            type="text"
                                            className="form-control"
                                            id="name"
                                            placeholder={t('Name')}
                                            value={formik?.values?.name}
                                            onChange={(event: any) => formik.setFieldValue('name', event?.target?.value)}
                                        />
                                        {formik.touched.name && formik.errors.name ? (
                                            <div className="text-danger mt-2">{formik.errors.name}</div>
                                        ) : null}
                                    </Col>
                                    <Col sm={12} className="mb-3">
                                        <Label htmlFor="content" className="form-label">{t('Template')} <span className="text-danger"> *</span></Label>
                                        <Input
                                            name="content"
                                            type="textarea"
                                            rows={6}
                                            className="form-control"
                                            id="content"
                                            placeholder={t('Template')}
                                            value={formik?.values?.content}
                                            onChange={(event: any) => formik.setFieldValue('content', event?.target?.value)}
                                        />
                                        {formik.touched.content && formik.errors.content ? (
                                            <div className="text-danger mt-2">{formik.errors.content}</div>
                                        ) : null}
                                    </Col>
                                    <Col sm={12} className="mb-3">
                                        <div className="form-check form-switch mb-0">
                                            <Input className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id={`generate_via_ai`}
                                                name={`generate_via_ai`}
                                                checked={!!(formik?.values?.generate_via_ai || 0)}
                                                onChange={(event: any) => {
                                                    formik.setFieldValue(`generate_via_ai`, Number(event?.target.checked))
                                                }}
                                            />
                                            <Label htmlFor={`generate_via_ai`} className="form-label mb-0">{t('Title Regeneration')}</Label>
                                        </div>
                                        {(formik.touched.generate_via_ai && formik.errors.generate_via_ai) ? (
                                            <div className="text-danger mt-2">{formik.errors.generate_via_ai || ''}</div>
                                        ) : null}
                                    </Col>

                                    <Col sm={12} className="mb-3">
                                        <Label htmlFor="generate_ai_model" className="form-label">{t('AI Model')} <span className="text-danger"> *</span> </Label>
                                        <DropdownOption
                                            name="generate_ai_model"
                                            dataList={AI_MODEL_OPTIONS_LANG || []}
                                            placeholder={`${t("AI Model")}...`}
                                            className={`dropdown-status-rounded z-hight`}
                                            classNamePrefix="name-prefix"
                                            initialValue={formik?.values?.generate_ai_model || AI_MODEL_OPTIONS_LANG[1]}
                                            onChangeSelect={(e: any) => {
                                                formik.setFieldValue(`generate_ai_model`, e)
                                            }}
                                        />
                                    </Col>

                                    <Col sm={12} className="mb-3">
                                        <Label htmlFor="generate_prompt" className="form-label">{t('Prompt')}</Label>
                                        <Input
                                            name="text"
                                            type="textarea"
                                            className={`form-control`}
                                            placeholder={`${t('Prompt')}...`}
                                            rows={6}
                                            style={{ width: '100%', minHeight: '100px' }}
                                            value={formik?.values?.generate_prompt}
                                            onChange={(event: any) => {
                                                formik.setFieldValue('generate_prompt', event?.target?.value)
                                            }}
                                            autoComplete="off"
                                        />
                                        <div className="text-muted fs-12 mt-1">
                                            {t('Source data is labeled as ‘{title}’.')}
                                        </div>
                                        {formik.touched.generate_prompt && formik.errors.generate_prompt ? (
                                            <div className="text-danger mt-2">{formik.errors.generate_prompt}</div>
                                        ) : null}
                                    </Col>

                                    <Col sm={12} className="mb-2" >
                                        <div className="d-flex gap-3 justify-content-end align-item-center">
                                            <button
                                                className="btn btn-primary fs-14 px-1 d-flex justify-content-center align-item-center"
                                                type="submit"
                                                style={{ width: '150px' }}
                                                disabled={isLoading}
                                            >
                                                {isLoading && <span style={{ transform: 'translateY(1px)' }} className="me-1"><Spinner size="sm"></Spinner></span>}<span>{
                                                    !!detail?.id ? t('Button Update Subject') : t('Button Create Subject')
                                                }</span>
                                            </button>
                                            <button
                                                className="btn btn-secondary fs-14 px-1 d-flex justify-content-center align-item-center"
                                                type="button"
                                                onClick={onCloseClick}
                                                style={{ width: '75px' }}
                                            >
                                                <i className="ri-close-fill" style={{ lineHeight: '22px' }} /> <span className="ms-1">{t("Button Close")}</span>
                                            </button>
                                        </div>
                                    </Col>
                                </Col>

                            </Row>
                        </form>
                    </div>
                </ModalBody>
            </Modal>
        </React.Fragment >
    );
};

export default SubjectForm;

